import * as THREE from 'three';

export type CraftRecipe = {
  id: string;
  label: string;
  cost: number;
  hint: string;
  mechParts?: number;
  monsterParts?: number;
};

export type QuickSidegradeState = {
  slot0Kind: 'ar' | 'smg' | 'pistol';
  slot0Rarity: string;
  shotgunRarity: string;
  sniperRarity: string;
};

/** Pick the best one-click sidegrade for the current loadout. */
export function resolveQuickSidegrade(state: QuickSidegradeState): string | null {
  if (state.slot0Kind === 'ar') return 'ar_to_smg';
  if (state.slot0Kind === 'smg') return 'smg_to_ar';
  const sgIdx = ['common', 'uncommon', 'rare', 'epic', 'legendary'].indexOf(state.shotgunRarity);
  if (sgIdx >= 0 && sgIdx < 4) return 'shotgun_upgrade';
  const srIdx = ['common', 'uncommon', 'rare', 'epic', 'legendary'].indexOf(state.sniperRarity);
  if (srIdx >= 0 && srIdx < 4) return 'sniper_upgrade';
  return null;
}

export const CRAFT_RECIPES: CraftRecipe[] = [
  { id: 'bandage',        label: 'Bandage',        cost: 15, hint: '+1 bandage' },
  { id: 'mini',           label: 'Mini Shield',    cost: 25, hint: '+1 mini shield' },
  { id: 'trap',           label: 'Spike Trap',     cost: 30, hint: 'Pre-built trap' },
  { id: 'launchpad',      label: 'Launch Pad',     cost: 40, hint: 'Pre-built launch pad' },
  { id: 'chug',           label: 'Chug Splash',    cost: 35, hint: '+1 chug splash' },
  { id: 'shock',          label: 'Shockwave',      cost: 45, hint: '+1 shockwave nade' },
  // ── Sidegrade / upgrade recipes (require weapon parts) ──────────────────────
  { id: 'ar_to_smg',       label: 'AR → SMG',       cost: 30, hint: 'Swap AR slot for SMG',        mechParts: 1 },
  { id: 'smg_to_ar',       label: 'SMG → AR',       cost: 30, hint: 'Swap SMG slot for AR',        mechParts: 1 },
  { id: 'shotgun_upgrade', label: 'Upgrade Shotgun', cost: 50, hint: 'Raise shotgun rarity',       mechParts: 2 },
  { id: 'sniper_upgrade',  label: 'Upgrade Sniper',  cost: 50, hint: 'Raise sniper rarity',        monsterParts: 2 },
];

// ── Spawn positions for collectible weapon-part pickups ──────────────────────

/** Four world positions where Mech Part cubes spawn (cyan bobbing cubes). */
export const MECH_PART_SPAWN_POSITIONS: Array<{ x: number; y: number; z: number }> = [
  { x: 38, y: 0.5, z: 12 },
  { x: 55, y: 0.5, z: 20 },
  { x: 30, y: 0.5, z: 30 },
  { x: 62, y: 0.5, z: 8  },
];

/** Four world positions where Monster Part cubes spawn (orange bobbing cubes). */
export const MONSTER_PART_SPAWN_POSITIONS: Array<{ x: number; y: number; z: number }> = [
  { x: -20, y: 0.5, z: 40 },
  { x: -35, y: 0.5, z: 25 },
  { x: -10, y: 0.5, z: 55 },
  { x: -45, y: 0.5, z: 12 },
];

const NEAR_RADIUS = 5;
const NEAR_RADIUS_SQ = NEAR_RADIUS * NEAR_RADIUS;

// Bench world position (Pleasant Park)
const BENCH_X = 40;
const BENCH_Y = 0;
const BENCH_Z = 15;

/** Overlay crafting menu at Pleasant Point bench — open with E, close with Esc. */
export class FpsCraftBench {
  private readonly backdrop: HTMLDivElement;
  private readonly panel: HTMLDivElement;
  private readonly matsLabel: HTMLSpanElement;
  private readonly mechPartsLabel: HTMLSpanElement;
  private readonly monsterPartsLabel: HTMLSpanElement;
  private readonly buttons: HTMLButtonElement[] = [];
  private readonly quickSidegradeBtn: HTMLButtonElement;
  private visible = false;

  // ── Weapon-parts inventory ───────────────────────────────────────────────
  mechPartsCount = 0;
  monsterPartsCount = 0;

  // ── World objects (set when scene is provided) ───────────────────────────
  private benchMesh: THREE.Mesh | null = null;
  private benchGlow: THREE.PointLight | null = null;

  // Bobbing collectible cubes
  private readonly mechPartMeshes: THREE.Mesh[] = [];
  private readonly monsterPartMeshes: THREE.Mesh[] = [];
  private elapsed = 0;

  constructor(
    mount: HTMLElement,
    private readonly getMats: () => number,
    private readonly onCraft: (recipeId: string) => boolean,
    scene?: THREE.Scene,
    private readonly getQuickSidegradeState?: () => QuickSidegradeState | null,
  ) {
    // ── World prop & collectibles ──────────────────────────────────────────
    if (scene) {
      this._buildWorldProp(scene);
      this._buildCollectibles(scene);
    }

    // ── UI ────────────────────────────────────────────────────────────────
    this.backdrop = document.createElement('div');
    this.backdrop.style.cssText = [
      'position:fixed',
      'inset:0',
      'z-index:95',
      'display:none',
      'align-items:center',
      'justify-content:center',
      'background:rgba(6,10,16,0.58)',
      'backdrop-filter:blur(7px)',
      '-webkit-backdrop-filter:blur(7px)',
      'pointer-events:auto',
    ].join(';');

    this.panel = document.createElement('div');
    this.panel.style.cssText = [
      'min-width:320px',
      'padding:18px 20px 16px',
      'border-radius:14px',
      'background:linear-gradient(165deg,rgba(22,34,28,0.97),rgba(10,16,14,0.98))',
      'border:2px solid rgba(68,255,136,0.62)',
      'box-shadow:0 10px 36px rgba(0,0,0,0.52),inset 0 1px 0 rgba(255,255,255,0.05)',
      'color:#e8f4ff',
      'font:14px system-ui,Segoe UI,sans-serif',
      'pointer-events:auto',
    ].join(';');

    const title = document.createElement('div');
    title.textContent = 'Crafting Bench';
    title.style.cssText =
      'font-weight:700;font-size:15px;letter-spacing:0.05em;margin-bottom:4px;color:#44ff88';
    this.panel.appendChild(title);

    const sub = document.createElement('div');
    sub.textContent = 'Pleasant Park · spend mats + parts to craft';
    sub.style.cssText = 'opacity:0.72;font-size:12px;margin-bottom:12px';
    this.panel.appendChild(sub);

    // ── Resource counters row ──────────────────────────────────────────────
    this.matsLabel        = document.createElement('span');
    this.mechPartsLabel   = document.createElement('span');
    this.monsterPartsLabel = document.createElement('span');

    this.matsLabel.style.cssText        = 'color:#f1c40f;font-weight:700';
    this.mechPartsLabel.style.cssText   = 'color:#00e5ff;font-weight:700';
    this.monsterPartsLabel.style.cssText = 'color:#ff7c2a;font-weight:700';

    const resRow = document.createElement('div');
    resRow.style.cssText = 'margin-bottom:14px;font-size:12px;display:flex;gap:14px;flex-wrap:wrap';

    const matsCell = document.createElement('span');
    matsCell.append('Mats: ', this.matsLabel);

    const mechCell = document.createElement('span');
    mechCell.append('⚙ Mech Parts: ', this.mechPartsLabel);

    const monsterCell = document.createElement('span');
    monsterCell.append('🦷 Monster Parts: ', this.monsterPartsLabel);

    resRow.append(matsCell, mechCell, monsterCell);
    this.panel.appendChild(resRow);

    this.quickSidegradeBtn = document.createElement('button');
    this.quickSidegradeBtn.type = 'button';
    this.quickSidegradeBtn.style.cssText = [
      'display:flex',
      'align-items:center',
      'justify-content:space-between',
      'width:100%',
      'margin-bottom:12px',
      'padding:12px 14px',
      'border-radius:11px',
      'border:2px solid rgba(68,255,136,0.72)',
      'background:linear-gradient(135deg,rgba(18,42,30,0.95),rgba(10,24,18,0.98))',
      'color:#e8f4ff',
      'font:800 13px system-ui,sans-serif',
      'cursor:pointer',
      'transition:background 0.12s,border-color 0.12s,opacity 0.12s,transform 0.08s',
      'box-shadow:0 4px 18px rgba(68,255,136,0.18)',
    ].join(';');
    this.quickSidegradeBtn.innerHTML =
      '<span>⚡ Quick Sidegrade</span><span style="opacity:0.82;font-weight:600;font-size:12px">One-click swap</span>';
    this.quickSidegradeBtn.addEventListener('click', () => {
      const id = this.getSuggestedQuickSidegrade();
      if (id && this.onCraft(id)) this.refresh();
    });
    this.quickSidegradeBtn.addEventListener('mouseenter', () => {
      if (!this.quickSidegradeBtn.disabled) {
        this.quickSidegradeBtn.style.background = 'linear-gradient(135deg,rgba(28,58,40,0.98),rgba(14,32,22,0.99))';
        this.quickSidegradeBtn.style.transform = 'translateY(-1px)';
      }
    });
    this.quickSidegradeBtn.addEventListener('mouseleave', () => {
      this.quickSidegradeBtn.style.background =
        'linear-gradient(135deg,rgba(18,42,30,0.95),rgba(10,24,18,0.98))';
      this.quickSidegradeBtn.style.transform = 'translateY(0)';
    });
    this.panel.appendChild(this.quickSidegradeBtn);

    // ── Recipe buttons ────────────────────────────────────────────────────
    const sectionBasic    = this._makeSectionLabel('Basic Crafting');
    const sectionSidegrade = this._makeSectionLabel('Weapon Sidegrades & Upgrades');
    this.panel.appendChild(sectionBasic);

    let sidegradeInserted = false;

    for (const recipe of CRAFT_RECIPES) {
      // Insert the sidegrade section header before the first sidegrade recipe
      if (!sidegradeInserted && (recipe.mechParts || recipe.monsterParts)) {
        this.panel.appendChild(sectionSidegrade);
        sidegradeInserted = true;
      }

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.style.cssText = [
        'display:flex',
        'align-items:center',
        'justify-content:space-between',
        'width:100%',
        'margin-bottom:8px',
        'padding:10px 12px',
        'border-radius:10px',
        'border:1px solid rgba(68,255,136,0.35)',
        'background:rgba(12,22,18,0.88)',
        'color:#e8f4ff',
        'font:600 13px system-ui,sans-serif',
        'cursor:pointer',
        'transition:background 0.12s,border-color 0.12s,opacity 0.12s',
      ].join(';');
      btn.dataset.recipeId = recipe.id;

      const costParts: string[] = [`${recipe.cost} mats`];
      if (recipe.mechParts)    costParts.push(`${recipe.mechParts}× <span style="color:#00e5ff">Mech Part</span>`);
      if (recipe.monsterParts) costParts.push(`${recipe.monsterParts}× <span style="color:#ff7c2a">Monster Part</span>`);

      btn.innerHTML =
        `<span>${recipe.label}</span>` +
        `<span style="opacity:0.82;font-weight:500;font-size:12px">${costParts.join(' · ')} · ${recipe.hint}</span>`;

      btn.addEventListener('click', () => {
        if (this.onCraft(recipe.id)) this.refresh();
      });
      btn.addEventListener('mouseenter', () => {
        if (!btn.disabled) btn.style.background = 'rgba(28,48,36,0.95)';
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.background = 'rgba(12,22,18,0.88)';
      });
      this.panel.appendChild(btn);
      this.buttons.push(btn);
    }

    const foot = document.createElement('div');
    foot.textContent = 'Esc — close';
    foot.style.cssText = 'margin-top:6px;text-align:center;opacity:0.55;font-size:11px';
    this.panel.appendChild(foot);

    this.backdrop.appendChild(this.panel);
    mount.appendChild(this.backdrop);
  }

  // ── Public weapon-parts API ──────────────────────────────────────────────

  addMechParts(n: number): void {
    this.mechPartsCount += n;
  }

  addMonsterParts(n: number): void {
    this.monsterPartsCount += n;
  }

  getSuggestedQuickSidegrade(): string | null {
    const state = this.getQuickSidegradeState?.();
    if (!state) return null;
    return resolveQuickSidegrade(state);
  }

  canAffordSidegrade(recipeId: string): boolean {
    const recipe = CRAFT_RECIPES.find((r) => r.id === recipeId);
    if (!recipe) return false;
    const mats = this.getMats();
    if (mats < recipe.cost) return false;
    if (recipe.mechParts    && this.mechPartsCount    < recipe.mechParts)    return false;
    if (recipe.monsterParts && this.monsterPartsCount < recipe.monsterParts) return false;
    return true;
  }

  /** Deduct weapon parts for a sidegrade (call AFTER mat deduction succeeds). */
  consumePartsForRecipe(recipeId: string): void {
    const recipe = CRAFT_RECIPES.find((r) => r.id === recipeId);
    if (!recipe) return;
    if (recipe.mechParts)    this.mechPartsCount    = Math.max(0, this.mechPartsCount    - recipe.mechParts);
    if (recipe.monsterParts) this.monsterPartsCount = Math.max(0, this.monsterPartsCount - recipe.monsterParts);
  }

  // ── Update loop (call each frame for bobbing collectibles) ───────────────

  update(dt: number): void {
    this.elapsed += dt;
    const t = this.elapsed;
    for (let i = 0; i < this.mechPartMeshes.length; i++) {
      const m = this.mechPartMeshes[i]!;
      const base = MECH_PART_SPAWN_POSITIONS[i]!;
      m.position.y = base.y + Math.sin(t * 2 + i) * 0.18;
      m.rotation.y = t * 1.2 + i;
    }
    for (let i = 0; i < this.monsterPartMeshes.length; i++) {
      const m = this.monsterPartMeshes[i]!;
      const base = MONSTER_PART_SPAWN_POSITIONS[i]!;
      m.position.y = base.y + Math.sin(t * 2.2 + i + 2) * 0.18;
      m.rotation.y = t * 1.4 + i;
    }
    // Gentle glow pulse on bench
    if (this.benchGlow) {
      this.benchGlow.intensity = 0.6 + Math.sin(t * 3) * 0.15;
    }
  }

  // ── Standard lifecycle ──────────────────────────────────────────────────

  isNearBench(px: number, pz: number, bx: number, bz: number): boolean {
    const dx = px - bx;
    const dz = pz - bz;
    return dx * dx + dz * dz <= NEAR_RADIUS_SQ;
  }

  getNearHint(): string {
    return '[E] Crafting Bench';
  }

  open(): void {
    this.visible = true;
    this.backdrop.style.display = 'flex';
    this.refresh();
  }

  close(): void {
    this.visible = false;
    this.backdrop.style.display = 'none';
  }

  isOpen(): boolean {
    return this.visible;
  }

  refresh(): void {
    const mats = this.getMats();
    this.matsLabel.textContent        = String(mats);
    this.mechPartsLabel.textContent   = String(this.mechPartsCount);
    this.monsterPartsLabel.textContent = String(this.monsterPartsCount);

    const quickId = this.getSuggestedQuickSidegrade();
    const quickRecipe = quickId ? CRAFT_RECIPES.find((r) => r.id === quickId) : null;
    if (quickRecipe && quickId) {
      const affordQuick = this.canAffordSidegrade(quickId);
      this.quickSidegradeBtn.disabled = !affordQuick;
      this.quickSidegradeBtn.style.opacity = affordQuick ? '1' : '0.45';
      this.quickSidegradeBtn.style.cursor = affordQuick ? 'pointer' : 'not-allowed';
      this.quickSidegradeBtn.style.display = 'flex';
      this.quickSidegradeBtn.innerHTML =
        `<span>⚡ ${quickRecipe.label}</span>` +
        `<span style="opacity:0.82;font-weight:600;font-size:12px">${quickRecipe.cost} mats · one-click</span>`;
    } else {
      this.quickSidegradeBtn.style.display = 'none';
    }

    for (const btn of this.buttons) {
      const id = btn.dataset.recipeId!;
      const recipe = CRAFT_RECIPES.find((r) => r.id === id)!;
      const afford =
        mats >= recipe.cost &&
        (recipe.mechParts    ? this.mechPartsCount    >= recipe.mechParts    : true) &&
        (recipe.monsterParts ? this.monsterPartsCount >= recipe.monsterParts : true);
      btn.disabled = !afford;
      btn.style.opacity     = afford ? '1' : '0.45';
      btn.style.cursor      = afford ? 'pointer' : 'not-allowed';
      btn.style.borderColor = afford ? 'rgba(68,255,136,0.45)' : 'rgba(255,96,96,0.35)';
    }
  }

  dispose(): void {
    this.backdrop.remove();
    if (this.benchMesh) {
      this.benchMesh.parent?.remove(this.benchMesh);
      (this.benchMesh.geometry as THREE.BufferGeometry).dispose();
      (this.benchMesh.material as THREE.Material).dispose();
    }
    if (this.benchGlow) this.benchGlow.parent?.remove(this.benchGlow);
    for (const m of [...this.mechPartMeshes, ...this.monsterPartMeshes]) {
      m.parent?.remove(m);
      (m.geometry as THREE.BufferGeometry).dispose();
      (m.material as THREE.Material).dispose();
    }
  }

  // ── Private helpers ──────────────────────────────────────────────────────

  private _makeSectionLabel(text: string): HTMLDivElement {
    const el = document.createElement('div');
    el.textContent = text;
    el.style.cssText =
      'font-size:11px;font-weight:700;letter-spacing:0.07em;text-transform:uppercase;' +
      'opacity:0.55;margin:8px 0 6px;padding-top:6px;border-top:1px solid rgba(255,255,255,0.08)';
    return el;
  }

  private _buildWorldProp(scene: THREE.Scene): void {
    // Workbench table: 1.5 wide × 0.8 tall × 0.8 deep, dark wood
    const geo = new THREE.BoxGeometry(1.5, 0.8, 0.8);
    const mat = new THREE.MeshStandardMaterial({
      color: 0x3b2510,
      roughness: 0.85,
      metalness: 0.05,
    });
    this.benchMesh = new THREE.Mesh(geo, mat);
    this.benchMesh.position.set(BENCH_X, BENCH_Y + 0.4, BENCH_Z);
    this.benchMesh.castShadow    = true;
    this.benchMesh.receiveShadow = true;
    scene.add(this.benchMesh);

    // Tabletop accent strip (lighter)
    const topGeo = new THREE.BoxGeometry(1.5, 0.06, 0.8);
    const topMat = new THREE.MeshStandardMaterial({ color: 0x5c3a1e, roughness: 0.7 });
    const topMesh = new THREE.Mesh(topGeo, topMat);
    topMesh.position.set(BENCH_X, BENCH_Y + 0.83, BENCH_Z);
    scene.add(topMesh);

    // Glow indicator (green point light above bench)
    this.benchGlow = new THREE.PointLight(0x44ff88, 0.7, 6);
    this.benchGlow.position.set(BENCH_X, BENCH_Y + 1.8, BENCH_Z);
    scene.add(this.benchGlow);
  }

  private _buildCollectibles(scene: THREE.Scene): void {
    const mechGeo     = new THREE.BoxGeometry(0.28, 0.28, 0.28);
    const mechMat     = new THREE.MeshStandardMaterial({ color: 0x00e5ff, emissive: 0x003344, roughness: 0.4, metalness: 0.6 });
    const monsterGeo  = new THREE.BoxGeometry(0.28, 0.28, 0.28);
    const monsterMat  = new THREE.MeshStandardMaterial({ color: 0xff7c2a, emissive: 0x331100, roughness: 0.5, metalness: 0.3 });

    for (const pos of MECH_PART_SPAWN_POSITIONS) {
      const m = new THREE.Mesh(mechGeo, mechMat);
      m.position.set(pos.x, pos.y, pos.z);
      m.castShadow = true;
      scene.add(m);
      this.mechPartMeshes.push(m);
    }

    for (const pos of MONSTER_PART_SPAWN_POSITIONS) {
      const m = new THREE.Mesh(monsterGeo, monsterMat);
      m.position.set(pos.x, pos.y, pos.z);
      m.castShadow = true;
      scene.add(m);
      this.monsterPartMeshes.push(m);
    }
  }
}
